import * as Sentry from '@sentry/react'
import { useRouter } from '@tanstack/react-router'
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Table,
  TableBody,
  TableCell,
  TableRow,
  cn
} from '@repo/ui'
import { Download, EllipsisVertical, Loader2, Plus, RefreshCw, Search, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import type { RagFile } from '@/api/content/file'
import { RagFileUploadState, getRagFileDownloadUrl, retryRagFileProcessing } from '@/api/content/file'
import { FileDeleteDialog, FileFormDialog } from '@/components/content/ContentDialogs'
import { ContentPagination } from './ContentPagination'
import { processingDetailsDisplay } from './processingDetails'
import { PageContent } from './PageContent'
import { PageHeader } from './PageHeader'
import type { BreadcrumbEntry } from './PageHeader'
import type { MouseEvent, ReactNode } from 'react'

function ContentBoundary({ embedded, children }: { embedded: boolean; children: ReactNode }) {
  return embedded ? children : <PageContent>{children}</PageContent>
}

function uploadStateLabel(state: RagFileUploadState): { label: string; colorClass: string } {
  switch (state) {
    case RagFileUploadState.Complete:
      return { label: 'Complete', colorClass: 'text-success' }
    case RagFileUploadState.Uploading:
      return { label: 'Uploading', colorClass: 'text-muted-foreground' }
    case RagFileUploadState.Processing:
      return { label: 'Processing', colorClass: 'text-muted-foreground' }
    case RagFileUploadState.Error:
      return { label: 'Error', colorClass: 'text-destructive' }
    default:
      return { label: state, colorClass: 'text-muted-foreground' }
  }
}

interface RagFilePageProps {
  breadcrumbs: Array<BreadcrumbEntry>
  items: Array<RagFile>
  projectId?: number
  currentPage: number
  pageCount: number
  documentCount?: number | undefined
  searchText: string
  onPageChange: (page: number) => void
  onSearchChange: (text: string) => void
  onClearDocId?: (() => void) | undefined
  assignmentControls?: Array<RagFileAssignmentControl> | undefined
  embedded?: boolean
  onAuthorityChanged?: () => void | Promise<void>
}

export interface RagFileAssignmentControl {
  assignedDocId: number | null
  label: string
  canManage: boolean
  canAssign: (item: RagFile) => boolean
  onAssign: (docId: number) => Promise<void>
  onClear: () => Promise<void>
}

export function RagFilePage({
  breadcrumbs,
  items,
  projectId,
  currentPage,
  pageCount,
  documentCount,
  searchText,
  onPageChange,
  onSearchChange,
  onClearDocId,
  assignmentControls,
  embedded = false,
  onAuthorityChanged
}: RagFilePageProps) {
  const router = useRouter()
  const refreshAuthority = useCallback(async () => {
    if (onAuthorityChanged !== undefined) await onAuthorityChanged()
    else await router.invalidate()
  }, [onAuthorityChanged, router])
  const [editItem, setEditItem] = useState<RagFile | null>(null)
  const [deleteItem, setDeleteItem] = useState<RagFile | null>(null)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [inputValue, setInputValue] = useState(searchText)
  const [prevSearchText, setPrevSearchText] = useState(searchText)
  const hasProcessingItems = items.some(
    (item) =>
      item.fileUploadState === RagFileUploadState.Processing || item.fileUploadState === RagFileUploadState.Uploading
  )

  if (prevSearchText !== searchText) {
    setPrevSearchText(searchText)
    setInputValue(searchText)
  }

  useEffect(() => {
    const trimmed = inputValue.trim()
    if (trimmed === searchText) return
    const id = setTimeout(() => onSearchChange(trimmed), 300)
    return () => clearTimeout(id)
  }, [inputValue, searchText, onSearchChange])

  useEffect(() => {
    if (!hasProcessingItems) return
    const id = setInterval(() => {
      void refreshAuthority()
    }, 10_000)
    return () => clearInterval(id)
  }, [hasProcessingItems, refreshAuthority])

  const totalFiles = documentCount ?? items.length
  const blockingDeleteAssignments =
    deleteItem && assignmentControls
      ? assignmentControls
          .filter((control) => control.assignedDocId === deleteItem.fileId)
          .map(({ label, canManage }) => ({ label, canManage }))
      : []

  return (
    <>
      {!embedded && <PageHeader breadcrumbs={breadcrumbs} />}
      <ContentBoundary embedded={embedded}>
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {totalFiles === 0
                ? searchText
                  ? 'No matching files.'
                  : 'No files yet.'
                : `${totalFiles} file${totalFiles === 1 ? '' : 's'}`}
            </p>
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <div className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
                <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Search files..."
                  className="w-full pr-9 pl-9"
                />
                {inputValue && (
                  <button
                    type="button"
                    onClick={() => setInputValue('')}
                    className="absolute top-1/2 right-2.5 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
              <Button size="sm" onClick={() => setShowAddDialog(true)}>
                <Plus className="size-4" />
                Add
              </Button>
            </div>
          </div>

          {onClearDocId && (
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
              <span className="text-muted-foreground">Showing a single document.</span>
              <button type="button" onClick={onClearDocId} className="font-medium underline hover:text-foreground">
                View all
              </button>
            </div>
          )}

          {items.length > 0 && (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableBody>
                  {items.map((item) => (
                    <FileRow
                      key={item.fileId}
                      item={item}
                      onEdit={() => setEditItem(item)}
                      onDelete={() => setDeleteItem(item)}
                      onAuthorityChanged={refreshAuthority}
                      {...(assignmentControls ? { assignmentControls } : {})}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {pageCount > 1 && (
            <ContentPagination currentPage={currentPage} pageCount={pageCount} onPageChange={onPageChange} />
          )}
        </div>
      </ContentBoundary>

      <FileFormDialog
        key={showAddDialog ? 'add' : 'add-closed'}
        open={showAddDialog}
        {...(projectId !== undefined ? { projectId } : {})}
        onSaved={refreshAuthority}
        onClose={() => setShowAddDialog(false)}
      />

      <FileFormDialog
        key={editItem ? `edit-${editItem.fileId}` : 'edit-closed'}
        open={!!editItem}
        {...(projectId !== undefined ? { projectId } : {})}
        {...(editItem ? { item: editItem } : {})}
        onSaved={refreshAuthority}
        onClose={() => setEditItem(null)}
      />

      <FileDeleteDialog
        item={deleteItem}
        blockingAssignments={blockingDeleteAssignments}
        {...(projectId !== undefined ? { projectId } : {})}
        onDeleted={refreshAuthority}
        onClose={() => setDeleteItem(null)}
      />
    </>
  )
}

function formatUpdatedAt(tsMs: number): string {
  return new Date(tsMs).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

function FileRow({
  item,
  onEdit,
  onDelete,
  assignmentControls,
  onAuthorityChanged
}: {
  item: RagFile
  onEdit: () => void
  onDelete: () => void
  assignmentControls?: Array<RagFileAssignmentControl>
  onAuthorityChanged: () => void | Promise<void>
}) {
  const [actionError, setActionError] = useState<string | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)
  const [assigningLabel, setAssigningLabel] = useState<string | null>(null)
  const stateInfo = uploadStateLabel(item.fileUploadState)

  async function handleDownload(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation()
    setActionError(null)
    try {
      const url = await getRagFileDownloadUrl(item.fileId)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      Sentry.captureException(err, { extra: { ragFileId: item.fileId } })
      setActionError(err instanceof Error ? err.message : 'Download failed')
    }
  }

  async function handleRetry(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation()
    setActionError(null)
    setIsRetrying(true)
    try {
      await retryRagFileProcessing(item.fileId)
      await onAuthorityChanged()
    } catch (err) {
      Sentry.captureException(err, { extra: { ragFileId: item.fileId } })
      setActionError(err instanceof Error ? err.message : 'Retry failed')
    } finally {
      setIsRetrying(false)
    }
  }

  const detailsDisplay = processingDetailsDisplay(item.processingDetails)
  const canRetry = item.fileUploadState === RagFileUploadState.Error
  const controls = assignmentControls ?? []

  async function handleAssignment(control: RagFileAssignmentControl, e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation()
    setActionError(null)
    setAssigningLabel(control.label)
    try {
      if (control.assignedDocId === item.fileId) {
        await control.onClear()
      } else {
        await control.onAssign(item.fileId)
      }
      await onAuthorityChanged()
    } catch (err) {
      Sentry.captureException(err, { extra: { ragFileId: item.fileId, assignment: control.label } })
      setActionError(err instanceof Error ? err.message : 'Assignment failed')
    } finally {
      setAssigningLabel(null)
    }
  }

  return (
    <TableRow className="cursor-pointer hover:bg-muted/50 [&_td]:p-4 [&_td]:align-top" onClick={onEdit}>
      <TableCell className="w-full">
        <div className="mb-1.5 flex items-center gap-2">
          <span className="text-sm font-medium">{item.fileName}</span>
          <span className={cn('text-xs font-medium', stateInfo.colorClass)}>{stateInfo.label}</span>
          {controls
            .filter((control) => control.assignedDocId === item.fileId)
            .map((control) => (
              <Badge key={control.label} variant="secondary">
                {control.label}
              </Badge>
            ))}
          {detailsDisplay && <span className={cn('text-xs', detailsDisplay.colourClass)}>{detailsDisplay.label}</span>}
        </div>
        {item.description && (
          <div className="mb-1 line-clamp-4 text-xs wrap-anywhere whitespace-normal">{item.description}</div>
        )}
        <div className="text-xs text-muted-foreground">Last updated: {formatUpdatedAt(item.updatedAtTsMs)}</div>
        {actionError && (
          <p role="alert" className="mt-1 text-xs text-destructive">
            {actionError}
          </p>
        )}
      </TableCell>
      <TableCell className="w-10 align-middle whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {`${item.updatedByFirstName} ${item.updatedBySurname}`.trim()}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" aria-label="File actions">
                <EllipsisVertical className="size-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  void handleDownload(e)
                }}
              >
                <Download className="size-4" />
                Download
              </DropdownMenuItem>
              {canRetry && (
                <DropdownMenuItem
                  disabled={isRetrying}
                  onClick={(e) => {
                    e.stopPropagation()
                    void handleRetry(e)
                  }}
                >
                  {isRetrying ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
                  Retry processing
                </DropdownMenuItem>
              )}
              {controls
                .filter(
                  (control) => control.canManage && (control.assignedDocId === item.fileId || control.canAssign(item))
                )
                .map((control) => {
                  const isAssigned = control.assignedDocId === item.fileId
                  const isAssigning = assigningLabel === control.label
                  return (
                    <DropdownMenuItem
                      key={control.label}
                      disabled={assigningLabel !== null}
                      onClick={(e) => {
                        e.stopPropagation()
                        void handleAssignment(control, e)
                      }}
                    >
                      {isAssigning ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : isAssigned ? (
                        `Remove as ${control.label.toLowerCase()}`
                      ) : (
                        `Set as ${control.label.toLowerCase()}`
                      )}
                    </DropdownMenuItem>
                  )
                })}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit()
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  )
}
